import React, { Component, Fragment } from 'react';
import propTypes from 'prop-types';
import { EditorState, Modifier } from 'draft-js';
import { getEntityRange, getSelectionEntity, getSelectionText } from 'draftjs-utils';
import {
  Form,
  FormGroup,
  Input,
  Label,
  Popover,
  PopoverBody,
  PopoverHeader
} from 'reactstrap';

import { noop } from 'lib/noop';
import { isBlank } from 'lib/string';
import { ClearButton, IconButton, PrimaryButton } from 'ui/Components';
import { link } from 'ui/Icons';

import { CHAT_LINK_TYPES, LINK_ENTITY_TYPE } from '../const';

/**
 * live text editor button with support for custom chat links
 */
export default class LiveTextLinkButton extends Component {
  static propTypes = {
    id: propTypes.string.isRequired,
    editorState: propTypes.object,
    onChange: propTypes.func
  };

  static defaultProps = {
    editorState: EditorState.createEmpty(),
    onChange: noop
  };

  static BLANK_LINK_STATE = {
    inputChanged: false,
    linkChat: CHAT_LINK_TYPES[0],
    linkNewTab: false,
    linkText: '',
    linkTitle: '',
    linkUrl: ''
  };

  static getDerivedStateFromProps(props, state) {
    // don't do anything when the input has changed
    if (state.inputChanged) {
      return null;
    }

    const { editorState } = props;
    const currentEntity = getSelectionEntity(editorState);
    const linkText = getSelectionText(editorState);

    // reset link state except for text when there's no entity
    if (!currentEntity) {
      return { ...LiveTextLinkButton.BLANK_LINK_STATE, linkText };
    }

    // fill inputs based on link entity data
    const entity = editorState.getCurrentContent().getEntity(currentEntity);
    if (entity.get('type') === LINK_ENTITY_TYPE) {
      const { target, title: linkTitle, url: linkUrl } = entity.get('data');
      const { text: linkText = '' } = getEntityRange(editorState, currentEntity) || {};

      const newState = {
        currentEntity,
        linkText,
        linkTitle,
        linkUrl,
        linkChat: CHAT_LINK_TYPES.includes(linkTitle) ? linkTitle : CHAT_LINK_TYPES[0],
        linkNewTab: target === '_blank'
      };

      return newState;
    }

    return null;
  }

  constructor(props) {
    super(props);

    this.state = {
      popoverOpen: false,
      ...LiveTextLinkButton.BLANK_LINK_STATE
    };

    this.addLink = this.addLink.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.openPopover = this.openPopover.bind(this);
    this.resetStateAndClose = this.resetStateAndClose.bind(this);
  }

  /**
   * update state based on link inputs
   * @param {Event} e - change event
   */
  handleInputChange(e) {
    const { checked, name, type, value } = e.target;

    const updateObj = {
      [name]: type === 'checkbox' ? checked : value,
      inputChanged: true
    };

    // update url and title to reflect chat
    if (name === 'linkChat') {
      updateObj.linkTitle = value;
      updateObj.linkUrl = '#';
    }

    this.setState(updateObj);
  }

  /**
   * create link entity based on state and update editor state
   */
  addLink() {
    const { editorState, onChange } = this.props;
    const { linkNewTab, linkText, linkTitle: title, linkUrl: url } = this.state;
    const isAbsoluteOrRelativeToDomain = /^(http|hlios|nordstrom|mailto|tel|#|\/)/.test(url);

    // create data object for link entity
    const linkData = {
      title,
      url: isAbsoluteOrRelativeToDomain ? url : `/${url}`
    };
    if (linkNewTab) {
      linkData.rel = 'noopener noreferrer';
      linkData.target = '_blank';
    }

    // via https://github.com/jpuri/react-draft-wysiwyg/blob/master/src/controls/Link/index.js
    let selection = editorState.getSelection();
    const currentEntity = getSelectionEntity(editorState);
    if (currentEntity) {
      const entityRange = getEntityRange(editorState, currentEntity);
      selection = selection.merge({
        anchorOffset: entityRange.start,
        focusOffset: entityRange.end
      });
    }

    const linkEntity = editorState
      .getCurrentContent()
      .createEntity(LINK_ENTITY_TYPE, 'MUTABLE', linkData)
      .getLastCreatedEntityKey();

    const contentState = Modifier.replaceText(
      editorState.getCurrentContent(),
      selection,
      linkText,
      editorState.getCurrentInlineStyle(),
      linkEntity
    );

    onChange(EditorState.push(editorState, contentState, 'insert-characters'));
    this.resetStateAndClose();
  }

  /**
   * open popover
   */
  openPopover() {
    this.setState({ popoverOpen: true });
  }

  /**
   * reset link state and close popover
   */
  resetStateAndClose() {
    this.setState({
      ...LiveTextLinkButton.BLANK_LINK_STATE,
      popoverOpen: false
    });
  }

  /**
   * render popover with link inputs
   * @returns {Component} popover
   */
  renderPopover() {
    const { id } = this.props;
    const { linkChat, linkNewTab, linkText, linkTitle, linkUrl, popoverOpen } = this.state;

    const isChatLink = !isBlank(linkChat);

    return (
      <Popover
        isOpen={popoverOpen}
        target={id}
        toggle={this.resetStateAndClose}
        trigger="legacy"
      >
        <PopoverHeader>Add Link</PopoverHeader>
        <PopoverBody>
          <Form inline className="mb-2">
            <Label className="font-weight-bold mr-2" size="sm">
              Link Text:
            </Label>
            <Input
              type="text"
              name="linkText"
              value={linkText}
              bsSize="sm"
              onChange={this.handleInputChange}
            />
          </Form>
          <Form inline className="mb-2">
            <Label className="font-weight-bold mr-2" size="sm">
              Link URL:
            </Label>
            <Input
              type="text"
              name="linkUrl"
              value={linkUrl}
              disabled={isChatLink}
              bsSize="sm"
              onChange={this.handleInputChange}
            />
          </Form>
          <Form inline className="mb-2">
            <Label className="font-weight-bold mr-2" size="sm">
              Link Title:
            </Label>
            <Input
              type="text"
              name="linkTitle"
              value={linkTitle}
              disabled={isChatLink}
              bsSize="sm"
              onChange={this.handleInputChange}
            />
          </Form>
          <Form inline className="mb-2">
            <Label className="font-weight-bold mr-2" size="sm">
              Chat Link:
            </Label>
            <Input
              type="select"
              name="linkChat"
              value={linkChat}
              bsSize="sm"
              onChange={this.handleInputChange}
            >
              {CHAT_LINK_TYPES.map(chat => (
                <option key={chat} value={chat}>
                  {chat}
                </option>
              ))}
            </Input>
          </Form>
          <FormGroup check inline className="mb-2">
            <Label check>
              <Input
                type="checkbox"
                name="linkNewTab"
                checked={linkNewTab}
                onChange={this.handleInputChange}
              />
              Open in new tab
            </Label>
          </FormGroup>
          <div>
            <PrimaryButton className="mr-2" onClick={this.addLink}>
              Add
            </PrimaryButton>
            <ClearButton onClick={this.resetStateAndClose}>Cancel</ClearButton>
          </div>
        </PopoverBody>
      </Popover>
    );
  }

  render() {
    const { id } = this.props;

    return (
      <Fragment>
        <IconButton
          className="rdw-option-wrapper"
          id={id}
          alt="Add Link"
          icon={link}
          noMargin
          rem={1.5}
          onClick={this.openPopover}
          data-test-id="liveTextEditor-linkButton"
        />
        {this.renderPopover()}
      </Fragment>
    );
  }
}
